import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { camerasApi } from '../../api/flashbackApi/cameras.ts';
import { HttpError } from '../../api/flashbackApi/http.ts';
import { startCameraRelay } from '../../api/cameras.ts';
import { recordingsApi } from '../../api/flashbackApi/recordings.ts';
import {
  getFlashbackDummyVideoUrl,
  isFlashbackDummyStreamEnabled,
} from '../../api/flashbackApi/flashbackDummyStream.ts';
import { useTimeline } from '../../hooks/flashbackHooks/useTimeline.ts';
import { formatLocalYmd, getViewerUtcOffsetMinutes } from '../../lib/date-ymd.ts';
import {
  isSegmentPlaybackAtEnd,
  playbackUiDurationSeconds,
  playlistSeekFromWallClockMs,
  resolveSegmentVideoSeekTargetSeconds,
  resolveTimelineSeekTargetSeconds,
  segmentPlaybackSecondsFromVideo,
  sortSegmentsChronological,
  wallClockMsForSegmentProgress,
} from '../../lib/timeline-playback.ts';
import {
  loadRecordingMp4,
  resolvePlaybackAtWallClock,
  wallClockMsFromSegmentPlayback,
} from '../../lib/recording-playback.ts';
import { startLiveHlsPlayback, type LiveHlsPlayback } from '../../lib/live-stream.ts';
import {
  getFlashbackView360Mode,
  persistFlashbackView360Mode,
  type View360Mode,
} from '../../lib/previewSession.ts';
import { Pano360WebGLVideo } from '../place360/Pano360WebGLVideo.tsx';
import { PlaybackVideoScrubber } from './PlaybackVideoScrubber.tsx';
import type { FlashbackPlaybackLocationState } from './FlashbackSectionTabs.tsx';
import type {
  Camera,
  CameraTimeline,
  ManualRecordingStatus,
  TimelineSegment,
} from '../../api/flashbackApi/types.ts';
import React from 'react';

interface Props {
  camera: Camera;
  initialDate?: string;
}

type PlayerPhase = 'loading' | 'playing' | 'offline' | 'error';

/** No-op (debug ingest removed). Keeps HMR bundles that still reference this from crashing. */
function dbgPlayback(..._args: unknown[]): void {}

const PLAYBACK_ZOOM_MIN = 1;
const PLAYBACK_ZOOM_MAX = 3;
const PLAYBACK_ZOOM_STEP = 0.25;
const PLAYBACK_PANO_FOV_BASE = 75;

function playbackPanoFovDeg(zoom: number): number {
  return Math.max(35, Math.min(90, PLAYBACK_PANO_FOV_BASE / zoom));
}

function streamSlotFlatZoomStyle(
  isMainSlot: boolean,
  useFlatZoom: boolean,
  zoom: number,
): React.CSSProperties | undefined {
  if (!isMainSlot || !useFlatZoom || zoom <= PLAYBACK_ZOOM_MIN) return undefined;
  return { transform: `scale(${zoom})` };
}

export function CameraPlaybackPanel({ camera, initialDate }: Props): React.ReactElement {
  const location = useLocation();
  const playbackNavState = (location.state as FlashbackPlaybackLocationState | null) ?? undefined;
  const videoRef = useRef<HTMLVideoElement>(null);
  const ptzVideoRef = useRef<HTMLVideoElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  /** Wall-clock seek target applied when the recording player mounts or segment changes. */
  const pendingSeekWallMsRef = useRef<number | null>(null);
  const loadRecordingAtWallClockRef = useRef<((wallMs: number) => void) | null>(null);
  /** Segment id currently loaded in the main recording `<video>` (for wall-clock sync). */
  const activeRecordingSegmentIdRef = useRef<string | null>(null);
  /** When HLS `duration` grows after manifest, re-apply a deep playlist seek once it fits. */
  const timelineSeekCatchupRef = useRef<(() => void) | null>(null);
  /** Set true in playback `useEffect` cleanup so duration catch-up does not run after unmount / mode switch. */
  const playbackCancelledRef = useRef(false);
  const previousTimelineDateRef = useRef<string>('');
  const timelineDataRef = useRef<CameraTimeline | null>(null);
  const ptzTimelineDataRef = useRef<CameraTimeline | null>(null);

  /** Opened from History or a past day — skip live relay until user picks Live explicitly. */
  const recordingPlaybackIntent = useMemo(() => {
    const today = formatLocalYmd(new Date());
    if (playbackNavState?.fromTab === 'history') return true;
    if (playbackNavState?.startTime?.trim()) return true;
    const navDate = playbackNavState?.date?.trim() || initialDate?.trim();
    if (navDate && navDate < today) return true;
    if (initialDate?.trim() && initialDate.trim() < today) return true;
    return false;
  }, [
    playbackNavState?.fromTab,
    playbackNavState?.startTime,
    playbackNavState?.date,
    initialDate,
  ]);

  const [selectedSegment, setSelectedSegment] = useState<TimelineSegment | null>(null);
  const isLive = selectedSegment === null && !recordingPlaybackIntent;
  const [playbackWallClockMs, setPlaybackWallClockMs] = useState<number | null>(null);
  const pendingNavStartTimeRef = useRef(playbackNavState?.startTime ?? null);
  const pendingHistorySegmentIdRef = useRef(playbackNavState?.segmentId ?? null);
  const historyAutoplayPendingRef = useRef(playbackNavState?.fromTab === 'history');
  const [phase, setPhase] = useState<PlayerPhase>('loading');
  const [playerError, setPlayerError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const [muted, setMuted] = useState(true);
  const [headerTick, setHeaderTick] = useState(0);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportHint, setExportHint] = useState<string | null>(null);
  const [mediaBusy, setMediaBusy] = useState(false);
  const [manualRecording, setManualRecording] = useState<ManualRecordingStatus>({
    active: camera.manualRecordingStartedAt != null,
    startedAt: camera.manualRecordingStartedAt,
  });
  const [manualRecordingBusy, setManualRecordingBusy] = useState(false);
  /** Mount PTZ element (live: when relay has PTZ URL; recording: while playing). */
  const [showPtzPip, setShowPtzPip] = useState(false);
  /** PTZ stream has loaded — show PiP layout and visible inset. */
  const [ptzPipVisible, setPtzPipVisible] = useState(false);
  const playbackEndedRef = useRef(false);
  /** Ignore progress sync while a scrub seek is in flight (avoids snapping back to 0). */
  const scrubSeekInFlightRef = useRef(false);
  /** Target `video.currentTime` seconds (within active segment) the user is seeking toward. */
  const scrubTargetSecRef = useRef<number | null>(null);
  /** Progress within the selected recording segment (drives the player scrubber). */
  const [segmentPlaybackSec, setSegmentPlaybackSec] = useState(0);
  /** Main video `duration` once known — keeps scrubber total in sync with the media element. */
  const [recordingMediaDurationSec, setRecordingMediaDurationSec] = useState<number | null>(null);
  /** Which lens is full-screen; the other stays in the inset preview. */
  const [mainLens, setMainLens] = useState<'360' | 'ptz'>('360');
  const [view360, setView360State] = useState<View360Mode>(() => getFlashbackView360Mode());
  const [playbackZoom, setPlaybackZoom] = useState(PLAYBACK_ZOOM_MIN);
  /** Avoid re-seeding wall-clock on timeline `data` polls; only seed when the selected segment id actually changes. */
  const lastSeededSegmentIdForPlaybackRef = useRef<string | null>(null);
  const pauseTimelinePolling = !isLive && phase === 'playing';

  const { data, date, isLoading, error, refresh } = useTimeline(camera.id, {
    initialDate,
    pausePolling: pauseTimelinePolling,
  });
  const { data: ptzTimelineData } = useTimeline(camera.id, {
    initialDate,
    stream: 'ptz',
    enabled: camera.dualStream && !isLive,
    pausePolling: pauseTimelinePolling,
  });

  timelineDataRef.current = data;
  ptzTimelineDataRef.current = ptzTimelineData;

  const mainSegmentsKey = useMemo(
    () => data?.segments.map((s) => s.id).join('|') ?? '',
    [data?.segments],
  );

  const ptzSegmentsKey = useMemo(
    () => ptzTimelineData?.segments.map((s) => s.id).join('|') ?? '',
    [ptzTimelineData?.segments],
  );

  const todayIso = useMemo(() => {
    void headerTick;
    return formatLocalYmd(new Date());
  }, [headerTick]);
  const showPlaybackOverlay = useMemo(() => {
    if (isLive) return phase !== 'playing';
    if (isLoading && !data?.segments.length) return true;
    return phase !== 'playing';
  }, [isLive, isLoading, phase, data?.segments.length]);

  const liveStreamLoadingLabel = useMemo(() => {
    if (!isLive || phase !== 'loading') return null;
    return 'Connecting…';
  }, [isLive, phase]);

  useEffect(() => {
    if (!data?.segments.length) return;
    const dateChanged = previousTimelineDateRef.current !== date;
    previousTimelineDateRef.current = date;

    const startTime = pendingNavStartTimeRef.current;
    const segmentIdHint = pendingHistorySegmentIdRef.current;
    if (startTime || segmentIdHint) {
      let seg: TimelineSegment | undefined;
      let wallMs: number | null = null;

      if (segmentIdHint) {
        seg = data.segments.find((s) => s.id === segmentIdHint);
      }
      if (startTime) {
        const parsed = new Date(startTime).getTime();
        if (!Number.isNaN(parsed)) {
          wallMs = parsed;
          const seek = playlistSeekFromWallClockMs(data.segments, parsed);
          if (!seg && seek.segmentId) {
            seg = data.segments.find((s) => s.id === seek.segmentId);
          }
        }
      }
      if (!seg && segmentIdHint) {
        seg = data.segments.find((s) => s.id === segmentIdHint);
      }
      if (seg) {
        if (wallMs === null) {
          wallMs = new Date(seg.startTime).getTime();
        }
        pendingSeekWallMsRef.current = wallMs;
        pendingNavStartTimeRef.current = null;
        pendingHistorySegmentIdRef.current = null;
        setPlaybackWallClockMs(wallMs);
        setSelectedSegment(seg);
        return;
      }
      pendingNavStartTimeRef.current = null;
      pendingHistorySegmentIdRef.current = null;
    }

    setSelectedSegment((current) => {
      if (current && data.segments.some((s) => s.id === current.id)) return current;
      if (
        dateChanged &&
        date < todayIso &&
        data.segments.length > 0 &&
        !pendingNavStartTimeRef.current
      ) {
        return sortSegmentsChronological(data.segments)[0];
      }
      return null;
    });
  }, [date, data, todayIso]);

  useEffect(() => {
    const id = window.setInterval(() => setHeaderTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    lastSeededSegmentIdForPlaybackRef.current = null;
    setPtzPipVisible(false);
    setShowPtzPip(false);
  }, [camera.id]);

  useEffect(() => {
    playbackEndedRef.current = false;
    setSegmentPlaybackSec(0);
    setRecordingMediaDurationSec(null);
    setPlaybackZoom(PLAYBACK_ZOOM_MIN);
  }, [date, selectedSegment?.id, isLive]);

  const loadPtzRecordingAtWallClockRef = useRef<
    (wallMs: number, signal?: AbortSignal) => Promise<boolean>
  >(async () => false);

  useEffect(() => {
    setPlaybackZoom(PLAYBACK_ZOOM_MIN);
  }, [camera.id]);

  const loadPtzRecordingAtWallClock = useCallback(
    async (wallMs: number, signal?: AbortSignal): Promise<boolean> => {
      if (!camera.dualStream || isLive) {
        return false;
      }
      const segments = ptzTimelineDataRef.current?.segments;
      if (!segments?.length) {
        return false;
      }
      const ptzVideo = ptzVideoRef.current;
      if (!ptzVideo) {
        return false;
      }
      const ptzResolved = resolvePlaybackAtWallClock(segments, wallMs);
      if (!ptzResolved) {
        return false;
      }
      await loadRecordingMp4(
        ptzVideo,
        ptzResolved.segment.playbackUrl,
        ptzResolved.offsetSeconds,
        { signal },
      );
      setPtzPipVisible(true);
      setShowPtzPip(true);
      return true;
    },
    [camera.dualStream, isLive],
  );

  useEffect(() => {
    loadPtzRecordingAtWallClockRef.current = loadPtzRecordingAtWallClock;
  }, [loadPtzRecordingAtWallClock]);

  /** Load PTZ recording when its timeline arrives or the selected segment changes (NOT on every wallclock tick). */
  useEffect(() => {
    if (isLive || !camera.dualStream || phase !== 'playing') {
      return undefined;
    }
    if (!ptzSegmentsKey) {
      return undefined;
    }
    if (!selectedSegment) {
      return undefined;
    }

    const wallMs = new Date(selectedSegment.startTime).getTime();
    if (Number.isNaN(wallMs)) {
      return undefined;
    }

    const controller = new AbortController();
    void loadPtzRecordingAtWallClock(wallMs, controller.signal).catch(() => undefined);

    return () => controller.abort();
  }, [
    isLive,
    camera.dualStream,
    phase,
    ptzSegmentsKey,
    selectedSegment,
    loadPtzRecordingAtWallClock,
  ]);

  useEffect(() => {
    setMainLens('360');
  }, [camera.id]);

  const toggleMainLens = useCallback(() => {
    if (!camera.dualStream) return;
    if (isLive && !showPtzPip) return;
    if (!isLive && !ptzPipVisible) return;
    setMainLens((l) => (l === '360' ? 'ptz' : '360'));
  }, [camera.dualStream, isLive, showPtzPip, ptzPipVisible]);

  const toggleView360 = useCallback(() => {
    const next: View360Mode = view360 === 'flat' ? 'immersive' : 'flat';
    setView360State(next);
    persistFlashbackView360Mode(next);
    if (next === 'flat') {
      setPlaybackZoom(PLAYBACK_ZOOM_MIN);
    }
  }, [view360]);

  const is360Camera = camera.type === '360';

  useEffect(() => {
    setManualRecording({
      active: camera.manualRecordingStartedAt != null,
      startedAt: camera.manualRecordingStartedAt,
    });
  }, [camera.id, camera.manualRecordingStartedAt]);

  const manualRecordingBusyRef = useRef(false);
  manualRecordingBusyRef.current = manualRecordingBusy;

  /**
   * Poll manual recording status in live mode only (not needed for recorded playback).
   */
  useEffect(() => {
    if (!isLive) return undefined;

    let cancelled = false;
    const controller = new AbortController();

    const fetchStatus = async (): Promise<void> => {
      if (manualRecordingBusyRef.current) return;
      try {
        const status = await camerasApi.getManualRecording(camera.id, controller.signal);
        if (!cancelled) setManualRecording(status);
      } catch (err) {
        if (axios.isCancel(err) || controller.signal.aborted) return;
      }
    };

    void fetchStatus();
    const id = window.setInterval(() => {
      void fetchStatus();
    }, 10_000);

    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(id);
    };
  }, [camera.id, isLive]);

  const headerDateTimeLabel = useMemo(() => {
    void headerTick;
    const ms =
      !isLive && playbackWallClockMs !== null
        ? playbackWallClockMs
        : !isLive && selectedSegment
          ? new Date(selectedSegment.startTime).getTime()
          : Date.now();
    return new Intl.DateTimeFormat(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }).format(new Date(ms));
  }, [isLive, playbackWallClockMs, selectedSegment, headerTick]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || isLive || !data?.segments.length || !selectedSegment?.id) {
      setPlaybackWallClockMs(null);
      setSegmentPlaybackSec(0);
      return undefined;
    }
    if (phase !== 'playing') {
      return undefined;
    }

    const segmentId = selectedSegment.id;

    const pauseAtSegmentEnd = (): void => {
      const ptz = ptzVideoRef.current;
      if (!video.paused) video.pause();
      if (ptz && !ptz.paused) ptz.pause();
      playbackEndedRef.current = true;
      const dur =
        Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;
      const seg = data.segments.find((s) => s.id === segmentId);
      if (seg) {
        const uiDur = playbackUiDurationSeconds(seg, dur);
        const finalSec = dur != null ? Math.min(uiDur, dur) : uiDur;
        setSegmentPlaybackSec(finalSec);
        setPlaybackWallClockMs(wallClockMsForSegmentProgress(seg, finalSec));
      }
    };

    const syncPlaybackProgress = (): void => {
      if (playbackEndedRef.current) {
        pauseAtSegmentEnd();
        return;
      }
      if (scrubSeekInFlightRef.current) {
        return;
      }
      if (video.seeking || video.readyState < HTMLMediaElement.HAVE_METADATA) return;

      if (scrubTargetSecRef.current !== null) {
        if (Math.abs(video.currentTime - scrubTargetSecRef.current) <= 0.6) {
          scrubTargetSecRef.current = null;
        } else {
          return;
        }
      }

      const dur =
        Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;
      if (dur != null) {
        setRecordingMediaDurationSec((prev) => (prev === dur ? prev : dur));
      }

      const activeId = activeRecordingSegmentIdRef.current ?? segmentId;
      const seg = data.segments.find((s) => s.id === activeId) ?? selectedSegment;
      if (!seg) return;

      const rel = segmentPlaybackSecondsFromVideo(
        data.segments,
        activeId,
        video.currentTime,
        dur,
      );
      setSegmentPlaybackSec(rel);
      setPlaybackWallClockMs(wallClockMsFromSegmentPlayback(seg, rel));

      if (isSegmentPlaybackAtEnd(data.segments, activeId, video.currentTime, dur, video.ended)) {
        pauseAtSegmentEnd();
      } else {
        playbackEndedRef.current = false;
      }
    };

    const onEnded = (): void => {
      pauseAtSegmentEnd();
    };

    syncPlaybackProgress();
    video.addEventListener('timeupdate', syncPlaybackProgress);
    video.addEventListener('seeked', syncPlaybackProgress);
    video.addEventListener('playing', syncPlaybackProgress);
    video.addEventListener('ended', onEnded);
    const tickId = window.setInterval(syncPlaybackProgress, 200);

    return () => {
      video.removeEventListener('timeupdate', syncPlaybackProgress);
      video.removeEventListener('seeked', syncPlaybackProgress);
      video.removeEventListener('playing', syncPlaybackProgress);
      video.removeEventListener('ended', onEnded);
      window.clearInterval(tickId);
    };
  }, [data, isLive, selectedSegment?.id, phase]);

  /**
   * Seed header wall-clock only when the selected segment *identity* changes.
   * Do not re-run on timeline `data` refetches — stale `endTime` from metadata would snap playback
   * time back to segment start while the user is viewing a later instant.
   */
  useEffect(() => {
    if (isLive) {
      lastSeededSegmentIdForPlaybackRef.current = null;
      return;
    }
    if (!selectedSegment?.id || !data?.segments.length) return;
    const seg = data.segments.find((s) => s.id === selectedSegment.id);
    if (!seg) return;
    const id = selectedSegment.id;
    if (lastSeededSegmentIdForPlaybackRef.current === id) return;
    lastSeededSegmentIdForPlaybackRef.current = id;
    setPlaybackWallClockMs((prev) => {
      const ws = new Date(seg.startTime).getTime();
      const we = new Date(seg.endTime).getTime();
      const keep = prev !== null && prev >= ws && prev <= we;
      return keep ? prev : ws;
    });
  }, [selectedSegment?.id, isLive, data]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || isLive || phase !== 'playing') {
      setMediaBusy(false);
      return undefined;
    }
    const onBusy = (): void => setMediaBusy(true);
    const onIdle = (): void => setMediaBusy(false);
    video.addEventListener('waiting', onBusy);
    video.addEventListener('playing', onIdle);
    video.addEventListener('canplay', onIdle);
    return () => {
      video.removeEventListener('waiting', onBusy);
      video.removeEventListener('playing', onIdle);
      video.removeEventListener('canplay', onIdle);
      setMediaBusy(false);
    };
  }, [isLive, phase, date, selectedSegment?.id, camera.id]);

  const seekVideosToSegmentSeconds = useCallback(
    (seconds: number, seg: TimelineSegment, segments: TimelineSegment[]) => {
      const main = videoRef.current;
      const ptz = ptzVideoRef.current;
      const videoDur =
        (main && Number.isFinite(main.duration) && main.duration > 0
          ? main.duration
          : null) ?? recordingMediaDurationSec;
      const durationSec = playbackUiDurationSeconds(seg, videoDur);
      const clamped = Math.min(durationSec, Math.max(0, seconds));
      const wallMs = wallClockMsForSegmentProgress(seg, clamped);

      playbackEndedRef.current = false;
      setSegmentPlaybackSec(clamped);
      setPlaybackWallClockMs(wallMs);

      const loadedId = activeRecordingSegmentIdRef.current;
      const canSeekInPlace =
        main &&
        (loadedId === seg.id || loadedId == null) &&
        main.readyState >= HTMLMediaElement.HAVE_METADATA;

      if (canSeekInPlace) {
        const target = resolveSegmentVideoSeekTargetSeconds(
          segments,
          seg,
          clamped,
          videoDur,
        );

        scrubSeekInFlightRef.current = true;
        scrubTargetSecRef.current = target;
        activeRecordingSegmentIdRef.current = seg.id;
        detachTimelineSeekCatchup(main, timelineSeekCatchupRef);
        main.currentTime = target;

        if (ptz && ptz.readyState >= HTMLMediaElement.HAVE_METADATA) {
          const ptzSegs = ptzTimelineDataRef.current?.segments;
          if (ptzSegs?.length) {
            const ptzResolved = resolvePlaybackAtWallClock(ptzSegs, wallMs);
            if (ptzResolved) {
              const ptzDur =
                Number.isFinite(ptz.duration) && ptz.duration > 0 ? ptz.duration : null;
              ptz.currentTime = resolveSegmentVideoSeekTargetSeconds(
                ptzSegs,
                ptzResolved.segment,
                ptzResolved.offsetSeconds,
                ptzDur,
              );
            }
          }
        }

        void main.play().catch(() => undefined);
        if (ptz) void ptz.play().catch(() => undefined);

        const releaseSeekFlag = (): void => {
          scrubSeekInFlightRef.current = false;
          main.removeEventListener('seeked', releaseSeekFlag);
          if (safetyTimer !== undefined) window.clearTimeout(safetyTimer);
        };
        const safetyTimer = window.setTimeout(releaseSeekFlag, 1500);
        main.addEventListener('seeked', releaseSeekFlag, { once: true });
        return true;
      }

      pendingSeekWallMsRef.current = wallMs;
      return false;
    },
    [recordingMediaDurationSec],
  );

  const seekToWallClockMs = useCallback(
    (wallMs: number) => {
      if (!data?.segments.length) return;
      const resolved = resolvePlaybackAtWallClock(data.segments, wallMs);
      if (!resolved) return;

      setPlaybackWallClockMs(wallMs);
      setSelectedSegment(resolved.segment);

      const startMs = new Date(resolved.segment.startTime).getTime();
      if (!Number.isNaN(startMs)) {
        const localSec = Math.max(0, (wallMs - startMs) / 1000);
        if (seekVideosToSegmentSeconds(localSec, resolved.segment, data.segments)) return;
      }

      if (phase === 'playing' && loadRecordingAtWallClockRef.current) {
        loadRecordingAtWallClockRef.current(wallMs);
        if (camera.dualStream) {
          setPtzPipVisible(false);
          void loadPtzRecordingAtWallClock(wallMs).catch(() => undefined);
        }
        return;
      }

      pendingSeekWallMsRef.current = wallMs;
    },
    [camera.dualStream, data, phase, loadPtzRecordingAtWallClock, selectedSegment?.id, seekVideosToSegmentSeconds],
  );

  const ensureRecordingPlaybackStarted = useCallback(() => {
    const main = videoRef.current;
    const ptz = ptzVideoRef.current;
    if (!main || isLive || playbackEndedRef.current) return;
    if (main.paused) void main.play().catch(() => undefined);
    if (ptz?.paused) void ptz.play().catch(() => undefined);
  }, [isLive]);

  useEffect(() => {
    if (!historyAutoplayPendingRef.current || isLive || phase !== 'playing') return undefined;
    ensureRecordingPlaybackStarted();
    const main = videoRef.current;
    if (!main) return undefined;
    const clearHistoryAutoplay = (): void => {
      historyAutoplayPendingRef.current = false;
    };
    if (!main.paused) {
      clearHistoryAutoplay();
      return undefined;
    }
    main.addEventListener('playing', clearHistoryAutoplay, { once: true });
    return () => main.removeEventListener('playing', clearHistoryAutoplay);
  }, [ensureRecordingPlaybackStarted, isLive, phase, selectedSegment?.id]);

  useEffect(() => {
    const startTime = pendingNavStartTimeRef.current;
    if (!startTime || !data?.segments.length) return;
    const wallMs = new Date(startTime).getTime();
    if (Number.isNaN(wallMs)) {
      pendingNavStartTimeRef.current = null;
      return;
    }
    pendingNavStartTimeRef.current = null;
    seekToWallClockMs(wallMs);
  }, [data, seekToWallClockMs]);

  const onSnapshot = useCallback(async () => {
    if (isLive && camera.status === 'active') {
      try {
        const blob = await recordingsApi.liveSnapshotJpeg(camera.id, { force: true });
        downloadBlob(blob, `snapshot-${camera.id}-${Date.now()}.jpg`);
      } catch (err) {
        const msg = err instanceof HttpError ? err.message : 'Snapshot failed';
        setExportHint(msg);
        window.setTimeout(() => setExportHint(null), 5000);
      }
      return;
    }

    const v = videoRef.current;
    if (!v || v.videoWidth === 0) return;
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    try {
      ctx.drawImage(v, 0, 0);
    } catch {
      return;
    }
    canvas.toBlob((blob) => {
      if (!blob) return;
      downloadBlob(blob, `snapshot-${camera.id}-${Date.now()}.png`);
    }, 'image/png');
  }, [camera.id, camera.status, isLive]);

  const onExportClip = useCallback(async () => {
    if (!data?.segments.length || exportBusy) return;

    const activeSegmentId = activeRecordingSegmentIdRef.current;
    const seg =
      selectedSegment && data
        ? data.segments.find((s) => s.id === (activeSegmentId ?? selectedSegment.id))
        : null;
    if (!seg || isLive) {
      setExportHint('Open a recording from History to export a clip.');
      window.setTimeout(() => setExportHint(null), 8000);
      return;
    }

    setExportHint(null);
    setExportBusy(true);
    try {
      const playbackMeta = parseSegmentPlaybackMeta(seg.playbackUrl);
      const payload: {
        cameraId: string;
        start?: string;
        end?: string;
        utcOffsetMinutes?: number;
        stream?: string;
        segmentName?: string;
        segmentStart?: string;
      } = {
        cameraId: camera.id,
        utcOffsetMinutes: getViewerUtcOffsetMinutes(),
      };

      if (playbackMeta?.name) {
        payload.segmentName = playbackMeta.name;
        payload.segmentStart = playbackMeta.segmentStart;
        payload.stream = playbackMeta.stream;
      } else {
        payload.start = new Date(seg.startTime).toISOString();
        payload.end = new Date(seg.endTime).toISOString();
      }

      const blob = await recordingsApi.exportClip(payload);
      downloadBlob(blob, `clip-${camera.id}-${Date.now()}.mp4`);
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Export failed';
      setExportHint(msg);
      window.setTimeout(() => setExportHint(null), 8000);
    } finally {
      setExportBusy(false);
    }
  }, [camera.id, data, exportBusy, isLive, selectedSegment]);

  const onToggleFullscreen = useCallback(() => {
    const el = frameRef.current;
    if (!el) return;
    if (!document.fullscreenElement) void el.requestFullscreen().catch(() => undefined);
    else void document.exitFullscreen();
  }, []);

  const onToggleManualRecording = useCallback(async () => {
    if (manualRecordingBusy) return;
    if (!manualRecording.active && camera.status !== 'active') {
      setExportHint('Camera is offline; activate it before starting manual recording.');
      window.setTimeout(() => setExportHint(null), 6000);
      return;
    }
    setManualRecordingBusy(true);
    try {
      const next = manualRecording.active
        ? await camerasApi.stopManualRecording(camera.id)
        : await camerasApi.startManualRecording(camera.id);
      setManualRecording(next);
    } catch (err) {
      const msg =
        err instanceof HttpError
          ? err.message
          : err instanceof Error
            ? err.message
            : manualRecording.active
              ? 'Failed to stop manual recording'
              : 'Failed to start manual recording';
      setExportHint(msg);
      window.setTimeout(() => setExportHint(null), 6000);
    } finally {
      setManualRecordingBusy(false);
    }
  }, [camera.id, camera.status, manualRecording.active, manualRecordingBusy]);

  const recordingScrub = useMemo(() => {
    if (isLive || !selectedSegment) return null;
    const startMs = new Date(selectedSegment.startTime).getTime();
    const endMs = new Date(selectedSegment.endTime).getTime();
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return null;
    const video = videoRef.current;
    const videoDur =
      recordingMediaDurationSec ??
      (video && Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null);
    const durationSec = playbackUiDurationSeconds(selectedSegment, videoDur);
    const currentSec = Math.min(durationSec, Math.max(0, segmentPlaybackSec));
    return { startMs, endMs, durationSec, currentSec };
  }, [isLive, selectedSegment, segmentPlaybackSec, recordingMediaDurationSec]);

  const onRecordingScrub = useCallback(
    (seconds: number) => {
      if (!recordingScrub || !selectedSegment || !data?.segments.length) return;
      const clamped = Math.min(recordingScrub.durationSec, Math.max(0, seconds));
      const wallMs = recordingScrub.startMs + clamped * 1000;

      if (seekVideosToSegmentSeconds(clamped, selectedSegment, data.segments)) return;

      pendingSeekWallMsRef.current = Math.min(
        recordingScrub.endMs,
        Math.max(recordingScrub.startMs, wallMs),
      );
      setSegmentPlaybackSec(clamped);
      setPlaybackWallClockMs(pendingSeekWallMsRef.current);
    },
    [recordingScrub, selectedSegment, data, seekVideosToSegmentSeconds],
  );

  const manualRecordingDurationLabel = useMemo(() => {
    void headerTick;
    if (!manualRecording.active || !manualRecording.startedAt) return null;
    const startedMs = new Date(manualRecording.startedAt).getTime();
    if (Number.isNaN(startedMs)) return null;
    const elapsedSec = Math.max(0, Math.floor((Date.now() - startedMs) / 1000));
    const hh = Math.floor(elapsedSec / 3600);
    const mm = Math.floor((elapsedSec % 3600) / 60);
    const ss = elapsedSec % 60;
    const pad = (n: number): string => String(n).padStart(2, '0');
    return hh > 0 ? `${pad(hh)}:${pad(mm)}:${pad(ss)}` : `${pad(mm)}:${pad(ss)}`;
  }, [manualRecording.active, manualRecording.startedAt, headerTick]);

  useEffect(() => {
    playbackCancelledRef.current = false;
    const video = videoRef.current;
    if (!video) return undefined;

    if (timelineSeekCatchupRef.current) {
      video.removeEventListener('durationchange', timelineSeekCatchupRef.current);
      timelineSeekCatchupRef.current = null;
    }

    const controller = new AbortController();
    let liveHlsPlayback: LiveHlsPlayback | null = null;
    let cancelled = false;
    let loadGeneration = 0;
    let activeMainSegmentId: string | null = null;

    setPhase('loading');
    setPlayerError(null);
    resetVideo(video);

    if (isFlashbackDummyStreamEnabled()) {
      const url = getFlashbackDummyVideoUrl();
      video.loop = true;
      video.src = url;
      void video
        .play()
        .then(() => {
          if (!cancelled) setPhase('playing');
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setPlayerError(toPlayerError(err, 'Could not play dummy preview video'));
          setPhase('error');
        });

      return () => {
        cancelled = true;
        playbackCancelledRef.current = true;
        controller.abort();
        resetVideo(video);
      };
    }

    if (isLive) {
      if (camera.status !== 'active') {
        setShowPtzPip(false);
        setPhase('offline');
        return () => controller.abort();
      }

      let pipPlayback: LiveHlsPlayback | null = null;

      startCameraRelay(camera.id)
        .then(async (relay) => {
          if (cancelled) return;
          const mainUrl = relay.hls360 ?? relay.hlsPtz;
          const pipUrl = relay.hls360 && relay.hlsPtz ? relay.hlsPtz : null;
          if (!mainUrl?.trim()) {
            throw new Error('No live HLS stream is available for this camera');
          }

          const usePip = Boolean(pipUrl?.trim());
          setShowPtzPip(camera.dualStream && usePip);
          setPtzPipVisible(false);

          liveHlsPlayback = await startLiveHlsPlayback(mainUrl, video, (message) => {
            if (cancelled) return;
            setPlayerError(message);
            setPhase('error');
          });
          if (cancelled) {
            liveHlsPlayback.destroy();
            return;
          }

          setPhase('playing');
          void video.play().catch(() => undefined);

          if (usePip && pipUrl) {
            await new Promise<void>((resolve) => {
              requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
            });
            const ptzVideo = ptzVideoRef.current;
            if (ptzVideo) {
              void startLiveHlsPlayback(pipUrl, ptzVideo, () => {
                /* PTZ PiP errors are non-fatal; main 360 stream keeps playing */
              })
                .then((playback) => {
                  if (cancelled) {
                    playback.destroy();
                    return;
                  }
                  pipPlayback = playback;
                  setPtzPipVisible(true);
                  void ptzVideo.play().catch(() => undefined);
                })
                .catch(() => undefined);
            }
          }
          void waitForVideoPlaybackStart(video, controller.signal).catch(() => undefined);
        })
        .catch((err: unknown) => {
          if (cancelled || controller.signal.aborted) return;
          setShowPtzPip(false);
          setPlayerError(toPlayerError(err, 'Failed to start live stream'));
          setPhase('error');
        });

      return () => {
        cancelled = true;
        controller.abort();
        setShowPtzPip(false);
        pipPlayback?.destroy();
        liveHlsPlayback?.destroy();
        resetVideo(video);
        if (ptzVideoRef.current) {
          resetVideo(ptzVideoRef.current);
        }
      };
    }

    if (!isLive && isLoading && !timelineDataRef.current?.segments.length) {
      setShowPtzPip(false);
      setPhase('loading');
      loadRecordingAtWallClockRef.current = null;
      return () => {
        cancelled = true;
        playbackCancelledRef.current = true;
        controller.abort();
        loadRecordingAtWallClockRef.current = null;
      };
    }

    const timeline = timelineDataRef.current;
    if (!timeline?.segments.length) {
      setShowPtzPip(false);
      setPhase('offline');
      loadRecordingAtWallClockRef.current = null;
      return () => {
        cancelled = true;
        playbackCancelledRef.current = true;
        controller.abort();
        loadRecordingAtWallClockRef.current = null;
      };
    }

    if (camera.dualStream) {
      setShowPtzPip(true);
      setPtzPipVisible(false);
    } else {
      setShowPtzPip(false);
      setPtzPipVisible(false);
    }

    const loadAtWallClock = async (wallMs: number): Promise<void> => {
      if (cancelled || controller.signal.aborted) return;
      const gen = ++loadGeneration;

      const segments = timelineDataRef.current?.segments;
      if (!segments?.length) return;
      const mainResolved = resolvePlaybackAtWallClock(segments, wallMs);
      if (!mainResolved) {
        setPlayerError('No recording at this time');
        setPhase('error');
        return;
      }

      setPlaybackWallClockMs(wallMs);
      activeMainSegmentId = mainResolved.segment.id;
      activeRecordingSegmentIdRef.current = mainResolved.segment.id;

      try {
        await loadRecordingMp4(video, mainResolved.segment.playbackUrl, mainResolved.offsetSeconds, {
          signal: controller.signal,
          onWaiting: () => setMediaBusy(true),
          onCanPlay: () => setMediaBusy(false),
        });
        if (cancelled || gen !== loadGeneration) return;

        setMediaBusy(false);
        setPhase('playing');
        void waitForVideoPlaybackStart(video, controller.signal).catch(() => undefined);

        if (camera.dualStream) {
          void loadPtzRecordingAtWallClockRef
            .current(wallMs, controller.signal)
            .catch(() => undefined);
        }
      } catch (err: unknown) {
        if (cancelled || controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setPlayerError(toPlayerError(err, 'Failed to load recording'));
        setPhase('error');
      }
    };

    loadRecordingAtWallClockRef.current = (wallMs: number) => {
      void loadAtWallClock(wallMs);
    };

    const onMainTimeUpdate = (): void => {
      if (cancelled || !activeMainSegmentId) return;
      const seg = timelineDataRef.current?.segments.find((s) => s.id === activeMainSegmentId);
      if (!seg || video.seeking) return;

      const wallMs = wallClockMsFromSegmentPlayback(seg, video.currentTime);
      setPlaybackWallClockMs(wallMs);

      const ptzVideo = ptzVideoRef.current;
      const ptzSegs = ptzTimelineDataRef.current?.segments;
      if (ptzSegs?.length && ptzVideo && !ptzVideo.seeking) {
        const ptzResolved = resolvePlaybackAtWallClock(ptzSegs, wallMs);
        if (ptzResolved) {
          const target = ptzResolved.offsetSeconds;
          if (Math.abs(ptzVideo.currentTime - target) > 0.35) {
            ptzVideo.currentTime = target;
          }
        }
      }
    };

    const onMainEnded = (): void => {
      playbackEndedRef.current = true;
      video.pause();
      ptzVideoRef.current?.pause();
    };

    video.addEventListener('timeupdate', onMainTimeUpdate);
    video.addEventListener('ended', onMainEnded);

    const navStartMs = pendingNavStartTimeRef.current
      ? new Date(pendingNavStartTimeRef.current).getTime()
      : Number.NaN;
    const startWallMs =
      pendingSeekWallMsRef.current ??
      (!Number.isNaN(navStartMs) ? navStartMs : null) ??
      (selectedSegment
        ? new Date(selectedSegment.startTime).getTime()
        : new Date(sortSegmentsChronological(timeline.segments)[0]!.startTime).getTime());
    pendingSeekWallMsRef.current = null;
    pendingNavStartTimeRef.current = null;

    void loadAtWallClock(startWallMs);

    return () => {
      cancelled = true;
      playbackCancelledRef.current = true;
      setShowPtzPip(false);
      setPtzPipVisible(false);
      loadRecordingAtWallClockRef.current = null;
      activeRecordingSegmentIdRef.current = null;
      video.removeEventListener('timeupdate', onMainTimeUpdate);
      video.removeEventListener('ended', onMainEnded);
      if (timelineSeekCatchupRef.current) {
        video.removeEventListener('durationchange', timelineSeekCatchupRef.current);
        timelineSeekCatchupRef.current = null;
      }
      controller.abort();
      resetVideo(video);
      if (ptzVideoRef.current) {
        resetVideo(ptzVideoRef.current);
      }
    };
  }, [
    camera.id,
    camera.dualStream,
    camera.status,
    date,
    isLive,
    mainSegmentsKey,
    retryToken,
  ]);

  const videoVisible = !(!isLive && isLoading) && phase === 'playing';
  /** Dual cameras always use two-slot DOM so 360 does not remount when PTZ loads. */
  const dualStreamShell = camera.dualStream;
  /** Swap control + PiP chrome when PTZ stream is actually ready. */
  const dualStreamLayout = dualStreamShell && ptzPipVisible && videoVisible;
  const pipTapRef = useRef<{ x: number; y: number } | null>(null);

  const isPipControlTarget = useCallback((target: EventTarget | null) => {
    const el = target as HTMLElement | null;
    if (!el) return false;
    return Boolean(
      el.closest('.ds-ptz-btn, button, input'),
    );
  }, []);

  const onPipShellPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!dualStreamLayout) return;
      if (isPipControlTarget(e.target)) return;
      pipTapRef.current = { x: e.clientX, y: e.clientY };
    },
    [dualStreamLayout, isPipControlTarget],
  );

  const onPipShellPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const tap = pipTapRef.current;
      pipTapRef.current = null;
      if (!dualStreamLayout || !tap || isPipControlTarget(e.target)) return;
      const dx = e.clientX - tap.x;
      const dy = e.clientY - tap.y;
      if (dx * dx + dy * dy < 400) {
        toggleMainLens();
      }
    },
    [dualStreamLayout, isPipControlTarget, toggleMainLens],
  );
  const show360Main =
    is360Camera && (!dualStreamLayout || mainLens === '360');
  const immersive360Active =
    show360Main && view360 === 'immersive' && videoVisible;
  const view360Label =
    view360 === 'flat' ? 'Switch to 360° immersive view' : 'Switch to flat view';
  /** Zoom for 360° main view: immersive uses FOV; flat strip uses scale. Not PTZ. */
  const zoomControlsEnabled = show360Main && videoVisible;
  const flat360ZoomActive = show360Main && view360 === 'flat' && videoVisible;
  const panoFovDeg = playbackPanoFovDeg(playbackZoom);
  const zoomInDisabled =
    !zoomControlsEnabled || playbackZoom >= PLAYBACK_ZOOM_MAX - 0.001;
  const zoomOutDisabled =
    !zoomControlsEnabled || playbackZoom <= PLAYBACK_ZOOM_MIN + 0.001;
  const zoomPctLabel = `${Math.round(playbackZoom * 100)}%`;

  const zoomInPlayback = useCallback(() => {
    setPlaybackZoom((z) =>
      Math.min(PLAYBACK_ZOOM_MAX, Math.round((z + PLAYBACK_ZOOM_STEP) * 100) / 100),
    );
  }, []);

  const zoomOutPlayback = useCallback(() => {
    setPlaybackZoom((z) =>
      Math.max(PLAYBACK_ZOOM_MIN, Math.round((z - PLAYBACK_ZOOM_STEP) * 100) / 100),
    );
  }, []);

  const onFrameWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      if (!zoomControlsEnabled || phase !== 'playing') return;
      if (e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -PLAYBACK_ZOOM_STEP : PLAYBACK_ZOOM_STEP;
      setPlaybackZoom((z) =>
        Math.min(
          PLAYBACK_ZOOM_MAX,
          Math.max(PLAYBACK_ZOOM_MIN, Math.round((z + delta) * 100) / 100),
        ),
      );
    },
    [phase, zoomControlsEnabled],
  );

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !is360Camera) return;
    v.crossOrigin = 'anonymous';
  }, [camera.id, is360Camera]);

  return (
    <div className="fb-playback__panel">
      <div
        ref={frameRef}
        className="fb-playback__frame"
        onWheel={onFrameWheel}
      >
        {dualStreamShell ? (
          <>
            <div
              className={[
                'fb-playback__stream-slot',
                mainLens === '360'
                  ? 'fb-playback__stream-slot--main'
                  : 'fb-playback__stream-slot--pip fb-playback__pip-shell--swappable',
              ].join(' ')}
              onPointerDown={
                mainLens === 'ptz' ? onPipShellPointerDown : undefined
              }
              onPointerUp={
                mainLens === 'ptz' ? onPipShellPointerUp : undefined
              }
              onPointerCancel={
                mainLens === 'ptz' ? onPipShellPointerUp : undefined
              }
            >
              <div
                className="fb-playback__zoom-layer"
                style={streamSlotFlatZoomStyle(
                  mainLens === '360',
                  flat360ZoomActive,
                  playbackZoom,
                )}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  muted={mainLens === 'ptz' ? true : muted}
                  playsInline
                  crossOrigin={is360Camera ? 'anonymous' : undefined}
                  className={[
                    mainLens === '360' ? 'fb-playback__video' : 'fb-playback__pip',
                    immersive360Active ? 'fb-playback__video--pano-source' : '',
                    videoVisible
                      ? mainLens === '360'
                        ? 'fb-playback__video--visible'
                        : 'fb-playback__pip--visible'
                      : mainLens === '360'
                        ? 'fb-playback__video--hidden'
                        : 'fb-playback__pip--hidden',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-label={
                    isLive ? '360° live preview' : '360° recording preview'
                  }
                />
                {immersive360Active ? (
                  <Pano360WebGLVideo
                    videoRef={videoRef}
                    enabled
                    fovDeg={panoFovDeg}
                  />
                ) : null}
              </div>
              {mainLens === 'ptz' ? (
                <span className="fb-playback__pip-label" aria-hidden>
                  360°
                </span>
              ) : null}
            </div>
            <div
              className={[
                'fb-playback__stream-slot',
                mainLens === 'ptz'
                  ? 'fb-playback__stream-slot--main'
                  : 'fb-playback__stream-slot--pip fb-playback__pip-shell--swappable',
              ].join(' ')}
              onPointerDown={
                mainLens === '360' ? onPipShellPointerDown : undefined
              }
              onPointerUp={
                mainLens === '360' ? onPipShellPointerUp : undefined
              }
              onPointerCancel={
                mainLens === '360' ? onPipShellPointerUp : undefined
              }
            >
              <div className="fb-playback__zoom-layer">
                <video
                  ref={ptzVideoRef}
                  autoPlay
                  muted
                  playsInline
                  className={[
                    mainLens === 'ptz' ? 'fb-playback__video' : 'fb-playback__pip',
                    (mainLens === 'ptz' ? videoVisible : ptzPipVisible && videoVisible)
                      ? mainLens === 'ptz'
                        ? 'fb-playback__video--visible'
                        : 'fb-playback__pip--visible'
                      : mainLens === 'ptz'
                        ? 'fb-playback__video--hidden'
                        : 'fb-playback__pip--hidden',
                  ].join(' ')}
                  aria-label={
                    isLive ? 'PTZ live preview' : 'PTZ recording preview'
                  }
                />
              </div>
              {mainLens === '360' && ptzPipVisible ? (
                <span className="fb-playback__pip-label" aria-hidden>
                  PTZ
                </span>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <div
              className="fb-playback__zoom-layer"
              style={streamSlotFlatZoomStyle(true, flat360ZoomActive, playbackZoom)}
            >
              <video
                ref={videoRef}
                autoPlay
                muted={muted}
                playsInline
                crossOrigin={is360Camera ? 'anonymous' : undefined}
                className={[
                  'fb-playback__video',
                  immersive360Active ? 'fb-playback__video--pano-source' : '',
                  videoVisible
                    ? 'fb-playback__video--visible'
                    : 'fb-playback__video--hidden',
                ]
                  .filter(Boolean)
                  .join(' ')}
              />
              {immersive360Active ? (
                <Pano360WebGLVideo videoRef={videoRef} enabled fovDeg={panoFovDeg} />
              ) : null}
            </div>
          </>
        )}

        <div className="fb-playback__overlay-top">
          <div className="fb-playback__overlay-row">
            <span className="fb-playback__camera-chip">
              {camera.name}
            </span>
            <div className="fb-playback__time-row">
              <span>{headerDateTimeLabel}</span>
              {dualStreamLayout ? (
                <button
                  type="button"
                  aria-label={
                    mainLens === '360'
                      ? 'Show PTZ full screen (360° in small preview)'
                      : 'Show 360° full screen (PTZ in small preview)'
                  }
                  title={mainLens === '360' ? 'Switch to PTZ view' : 'Switch to 360° view'}
                  className="fb-playback__icon-btn fb-playback__icon-btn--primary"
                  onClick={toggleMainLens}
                >
                  <SwitchCameraIcon />
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div
          className={[
            'fb-playback__overlay-bottom',
            recordingScrub ? 'fb-playback__overlay-bottom--with-scrubber' : '',
          ].join(' ')}
        >
          {recordingScrub ? (
            <div className="fb-playback__scrubber-wrap">
              <PlaybackVideoScrubber
                currentSeconds={recordingScrub.currentSec}
                durationSeconds={recordingScrub.durationSec}
                disabled={phase === 'offline' || phase === 'error'}
                onSeek={onRecordingScrub}
              />
            </div>
          ) : null}
          <div className="fb-playback__overlay-row fb-playback__overlay-row--bottom">
            <div className="fb-playback__status-group">
              <div className="fb-playback__live-badge">
                <span
                  className={`fb-playback__live-dot ${isLive ? 'fb-playback__live-dot--live' : 'fb-playback__live-dot--playback'}`}
                  aria-hidden
                />
                {isLive ? 'Live' : 'Playback'}
              </div>
              {isLive && manualRecording.active ? (
                <div
                  role="status"
                  aria-label={`Manual recording active${
                    manualRecordingDurationLabel ? ` (${manualRecordingDurationLabel})` : ''
                  }`}
                  className="fb-playback__rec-badge"
                >
                  <span className="fb-playback__rec-dot" aria-hidden />
                  REC
                  {manualRecordingDurationLabel ? (
                    <span style={{ fontVariantNumeric: "tabular-nums" }}>{manualRecordingDurationLabel}</span>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="fb-playback__controls-right">
            <div className="fb-playback__toolbar-col">
              {exportHint ? (
                <span className="fb-playback__export-hint">
                  {exportHint}
                </span>
              ) : null}
              <div className="fb-playback__toolbar">
              {isLive ? (
                <RoundVideoAction
                  label={
                    manualRecordingBusy
                      ? manualRecording.active
                        ? 'Stopping manual recording…'
                        : 'Starting manual recording…'
                      : manualRecording.active
                        ? `Stop manual recording${
                            manualRecordingDurationLabel ? ` (${manualRecordingDurationLabel})` : ''
                          }`
                        : camera.status !== 'active'
                          ? 'Camera is offline'
                          : 'Start manual recording'
                  }
                  disabled={manualRecordingBusy || (!manualRecording.active && camera.status !== 'active')}
                  active={manualRecording.active}
                  onClick={() => void onToggleManualRecording()}
                >
                  <ManualRecordIcon busy={manualRecordingBusy} active={manualRecording.active} />
                </RoundVideoAction>
              ) : null}
              <RoundVideoAction
                label={
                  isLive
                    ? 'Save snapshot (camera stream)'
                    : 'Save snapshot from video frame'
                }
                onClick={() => void onSnapshot()}
              >
                <SnapshotIcon />
              </RoundVideoAction>
              <RoundVideoAction
                label={
                  exportBusy
                    ? 'Exporting clip…'
                    : isLive
                      ? 'Export MP4 — open a recording from History first'
                      : 'Export MP4 — download current recording clip'
                }
                disabled={!data?.segments.length || exportBusy}
                onClick={() => void onExportClip()}
              >
                <ExportClipIcon busy={exportBusy} />
              </RoundVideoAction>
              {show360Main ? (
                <RoundVideoAction
                  label={view360Label}
                  onClick={toggleView360}
                  active={view360 === 'immersive'}
                >
                  {view360 === 'flat' ? <Icon360 /> : <IconFlat />}
                </RoundVideoAction>
              ) : null}
              {zoomControlsEnabled ? (
                <>
                  <RoundVideoAction
                    label={`Zoom out (${zoomPctLabel})`}
                    disabled={zoomOutDisabled}
                    onClick={zoomOutPlayback}
                  >
                    <ZoomOutIcon />
                  </RoundVideoAction>
                  <RoundVideoAction
                    label={`Zoom in (${zoomPctLabel})`}
                    disabled={zoomInDisabled}
                    onClick={zoomInPlayback}
                  >
                    <ZoomInIcon />
                  </RoundVideoAction>
                </>
              ) : null}
              <RoundVideoAction
                label={muted ? 'Unmute' : 'Mute'}
                onClick={() => setMuted((m) => !m)}
              >
                {muted ? <VolumeMutedIcon /> : <VolumeIcon />}
              </RoundVideoAction>
              <RoundVideoAction label="Fullscreen" onClick={onToggleFullscreen}>
                <FullscreenIcon />
              </RoundVideoAction>
              </div>
            </div>
            </div>
          </div>
        </div>

        {showPlaybackOverlay ? (
          <div className="fb-playback__player-overlay">
            <div className="fb-playback__player-overlay-inner">
              {phase === 'error' ? (
                <>
                  <span className="fb-playback__error-title">
                    {isLive ? 'Stream unavailable' : 'Playback unavailable'}
                  </span>
                  {playerError ? (
                    <span className="fb-playback__error-msg">{playerError}</span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      refresh();
                      setRetryToken((value) => value + 1);
                    }}
                    className="fb-playback__overlay-retry"
                  >
                    Retry
                  </button>
                </>
              ) : null}
              {phase === 'offline' ? (
                <span className="fb-playback__muted-label">Camera offline</span>
              ) : null}
              {phase !== 'error' &&
              phase !== 'offline' &&
              (phase === 'loading' || (!isLive && isLoading && !data?.segments.length)) ? (
                <>
                  <PlaybackSpinner className="fb-playback__icon fb-playback__icon--lg" />
                  <span className="fb-playback__muted-label">
                    {isLive
                      ? liveStreamLoadingLabel ?? 'Connecting…'
                      : isLoading && !data?.segments.length
                        ? 'Loading timeline…'
                        : 'Preparing playback…'}
                  </span>
                </>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function PlaybackSpinner({ className }: { className?: string }): React.ReactElement {
  return (
    <svg className={`fb-playback__spin ${className ?? ''}`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="fb-playback__spin-track" cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path
        d="M21 12a9 9 0 00-9-9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function RoundVideoAction({
  label,
  onClick,
  disabled,
  active,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  children: ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active ? true : undefined}
      title={label}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onClick();
      }}
      className={[
        'fb-playback__round-btn',
        active ? 'fb-playback__round-btn--active' : '',
        disabled ? 'fb-playback__round-btn--disabled' : '',
      ].filter(Boolean).join(' ')}
    >
      {children}
    </button>
  );
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function ExportClipIcon({ busy }: { busy: boolean }): React.ReactElement {
  if (busy) {
    return (
      <svg className="fb-playback__icon fb-playback__spin" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle
          className="fb-playback__spin-track"
          cx="12"
          cy="12"
          r="9"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path
          d="M21 12a9 9 0 00-9-9"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  return (
    <svg className="fb-playback__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 3v12" strokeLinecap="round" />
      <path d="M8 11l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 19h14" strokeLinecap="round" />
    </svg>
  );
}

function ManualRecordIcon({ busy, active }: { busy: boolean; active: boolean }): React.ReactElement {
  if (busy) {
    return (
      <svg className="fb-playback__icon fb-playback__spin" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle
          className="fb-playback__spin-track"
          cx="12"
          cy="12"
          r="9"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path
          d="M21 12a9 9 0 00-9-9"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  if (active) {
    return (
      <svg className="fb-playback__icon" viewBox="0 0 24 24" aria-hidden>
        <rect x="6" y="6" width="12" height="12" rx="1.5" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg className="fb-playback__icon" viewBox="0 0 24 24" aria-hidden>
      <circle cx="12" cy="12" r="5" fill="currentColor" />
    </svg>
  );
}

function ZoomInIcon(): React.ReactElement {
  return (
    <svg className="fb-playback__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.5-4.5" strokeLinecap="round" />
      <path d="M11 8v6M8 11h6" strokeLinecap="round" />
    </svg>
  );
}

function ZoomOutIcon(): React.ReactElement {
  return (
    <svg className="fb-playback__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.5-4.5" strokeLinecap="round" />
      <path d="M8 11h6" strokeLinecap="round" />
    </svg>
  );
}

function SnapshotIcon(): React.ReactElement {
  return (
    <svg className="fb-playback__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 7h4l2-2h4l2 2h4v12H4V7z" strokeLinejoin="round" />
      <circle cx="12" cy="13" r="3.5" />
    </svg>
  );
}

function VolumeIcon(): React.ReactElement {
  return (
    <svg className="fb-playback__icon" viewBox="0 0 24 24" fill="currentColor">
      <path d="M11 5L6 9H3v6h3l5 4V5zm7.5 3.5a5 5 0 010 7M17 9a3 3 0 010 6" />
    </svg>
  );
}

function VolumeMutedIcon(): React.ReactElement {
  return (
    <svg className="fb-playback__icon" viewBox="0 0 24 24" fill="currentColor">
      <path d="M11 5L6 9H3v6h3l5 4V5z" />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        d="M17 9l6 6M23 9l-6 6"
      />
    </svg>
  );
}

function FullscreenIcon(): React.ReactElement {
  return (
    <svg className="fb-playback__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SwitchCameraIcon(): React.ReactElement {
  return (
    <svg
      className="fb-playback__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <path d="M4 7h13l-2-2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20 17H7l2 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const view360IconProps = {
  className: 'fb-playback__icon',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function Icon360(): React.ReactElement {
  return (
    <svg {...view360IconProps}>
      <ellipse cx="12" cy="12" rx="10" ry="5" />
      <path d="M2 12c0-2.8 4.5-5 10-5" />
    </svg>
  );
}

function IconFlat(): React.ReactElement {
  return (
    <svg {...view360IconProps}>
      <rect x="3" y="6" width="18" height="12" rx="1.5" />
      <path d="M3 12h18" />
    </svg>
  );
}

const LIVE_STREAM_READY_TIMEOUT_MS = 20_000;

function waitForVideoPlaybackStart(
  video: HTMLVideoElement,
  signal?: AbortSignal,
): Promise<void> {
  if (signal?.aborted) {
    return Promise.reject(new DOMException('Aborted', 'AbortError'));
  }

  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && !video.paused) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const onPlaying = (): void => {
      cleanup();
      resolve();
    };
    const onError = (): void => {
      cleanup();
      reject(new Error('Stream failed to start'));
    };
    const onAbort = (): void => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const timeoutId = window.setTimeout(() => {
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        cleanup();
        resolve();
        return;
      }
      cleanup();
      reject(new Error('Stream connection timed out'));
    }, LIVE_STREAM_READY_TIMEOUT_MS);

    const cleanup = (): void => {
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('error', onError);
      signal?.removeEventListener('abort', onAbort);
      window.clearTimeout(timeoutId);
    };

    video.addEventListener('playing', onPlaying, { once: true });
    video.addEventListener('error', onError, { once: true });
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function resetVideo(video: HTMLVideoElement): void {
  video.pause();
  video.removeAttribute('src');
  video.srcObject = null;
  video.load();
}

function detachTimelineSeekCatchup(
  video: HTMLVideoElement,
  ref: MutableRefObject<(() => void) | null>,
): void {
  if (ref.current) {
    video.removeEventListener('durationchange', ref.current);
    ref.current = null;
  }
}

type SeekAndPlayOpts = {
  seekCatchupRef?: MutableRefObject<(() => void) | null>;
  catchupCancelled?: () => boolean;
};

function registerDurationSeekCatchup(
  video: HTMLVideoElement,
  want: number,
  ref: MutableRefObject<(() => void) | null>,
  cancelled: () => boolean,
): void {
  detachTimelineSeekCatchup(video, ref);
  if (want <= 0) return;

  let fires = 0;
  const handler = (): void => {
    if (cancelled() || ++fires > 48) {
      detachTimelineSeekCatchup(video, ref);
      return;
    }
    const d = video.duration;
    if (!Number.isFinite(d) || d <= 0) return;
    if (want > d - 0.05) return;
    if (Math.abs(video.currentTime - want) <= 1.5) {
      detachTimelineSeekCatchup(video, ref);
      return;
    }
    detachTimelineSeekCatchup(video, ref);
    seekAndPlay(video, want, {
      seekCatchupRef: ref,
      catchupCancelled: cancelled,
    });
  };
  ref.current = handler;
  video.addEventListener('durationchange', handler);
}

function seekAndPlay(video: HTMLVideoElement, offsetSeconds: number, opts?: SeekAndPlayOpts): void {
  const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;
  const target = resolveTimelineSeekTargetSeconds(offsetSeconds, duration);
  const cancelled = opts?.catchupCancelled ?? (() => false);
  if (opts?.seekCatchupRef) {
    if (offsetSeconds > 0 && (duration == null || offsetSeconds > duration - 0.15)) {
      registerDurationSeekCatchup(video, offsetSeconds, opts.seekCatchupRef, cancelled);
    } else {
      detachTimelineSeekCatchup(video, opts.seekCatchupRef);
    }
  }
  video.currentTime = target;
  void video.play().catch(() => {
    requestAnimationFrame(() => {
      void video.play().catch(() => undefined);
    });
  });
}

function toPlayerError(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

function parseSegmentPlaybackMeta(
  playbackUrl: string | undefined,
): { name: string; segmentStart?: string; stream?: string } | null {
  if (!playbackUrl?.trim()) {
    return null;
  }
  try {
    const parsed = new URL(playbackUrl, window.location.origin);
    const name = parsed.searchParams.get('name')?.trim() ?? '';
    if (!name) {
      return null;
    }
    const segmentStart = parsed.searchParams.get('segmentStart')?.trim() || undefined;
    const stream = parsed.searchParams.get('stream')?.trim() || undefined;
    return { name, segmentStart, stream };
  } catch {
    return null;
  }
}
